import React, { useEffect, useState, useRef } from "react";
import styled, { css, keyframes } from "styled-components";
import { IoMdArrowRoundForward } from "react-icons/io";
import { IoArrowForward, IoArrowBack } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./Button";

const kenBurnsEffect = keyframes`
  0% {
    transform: scale(1) translateX(0px) translateY(0px);
  }
  25% {
    transform: scale(1.08) translateX(-20px) translateY(-10px);
  }
  50% {
    transform: scale(1.12) translateX(20px) translateY(-20px);
  }
  75% {
    transform: scale(1.06) translateX(-10px) translateY(10px);
  }
  100% {
    transform: scale(1.1) translateX(0px) translateY(0px);
  }
`;

const textGlow = keyframes`
  0%, 100% {
    text-shadow: 
      0 0 5px rgba(255, 255, 255, 0.5),
      0 0 10px rgba(255, 255, 255, 0.3),
      0 0 15px rgba(255, 255, 255, 0.2),
      0 2px 4px rgba(0, 0, 0, 0.8);
  }
  50% {
    text-shadow: 
      0 0 8px rgba(255, 255, 255, 0.8),
      0 0 16px rgba(255, 255, 255, 0.5),
      0 0 24px rgba(255, 255, 255, 0.3),
      0 2px 6px rgba(0, 0, 0, 0.9);
  }
`;

const HeroSection = styled.section`
  height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
  background: #000;
`;

const HeroWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
`;

const HeroSlide = styled.div`
  z-index: 1;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const ImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #1a1a1a;
`;

const LoadingPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, #2a2a2a, #3a3a3a, #2a2a2a);
  background-size: 400% 400%;
  animation: shimmer 2s ease-in-out infinite;
  
  @keyframes shimmer {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const HeroSlider = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    z-index: 2;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.2) 30%,
      rgba(0, 0, 0, 0.1) 50%,
      rgba(0, 0, 0, 0.2) 70%,
      rgba(0, 0, 0, 0.8) 100%
    );
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    z-index: 3;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 30% 30%, transparent 20%, rgba(0, 0, 0, 0.1) 50%),
      radial-gradient(circle at 70% 70%, transparent 20%, rgba(0, 0, 0, 0.1) 50%);
    pointer-events: none;
  }
`;

const HeroImage = styled(motion.img)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform-origin: center;
  animation: ${kenBurnsEffect} 15s ease-in-out infinite;
  filter: contrast(1.1) saturate(1.1) brightness(0.9);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  width: calc(100% - 40px);
  color: #fff;
  align-items: center;
  text-align: center;
  justify-content: center;

  h3 {
    font-size: clamp(2.5rem, 7vw, 5.5rem);
    font-weight: 200;
    text-transform: none;
    text-align: center;
    margin-bottom: 2rem;
    line-height: 1.1;
    letter-spacing: -0.03em;
    color: white;
    background: none;
    padding: 0;
    border-radius: 0;
    opacity: 1;
    animation: ${textGlow} 3s ease-in-out infinite;
    font-family: 'Georgia', serif;
    text-shadow: 
      0 2px 4px rgba(0, 0, 0, 0.9),
      0 4px 8px rgba(0, 0, 0, 0.6),
      0 8px 16px rgba(0, 0, 0, 0.3);
    
    &::before {
      content: '';
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.4) 0%, transparent 70%);
      z-index: -1;
      border-radius: 20px;
      filter: blur(20px);
    }
  }

  h2 {
    font-size: clamp(1.5rem, 4vw, 2.8rem);
    font-weight: 300;
    color: rgba(255, 255, 255, 0.95);
    background: none;
    padding: 0px 25px 25px 25px;
    border-radius: 50px;
    opacity: 1;
    line-height: 1.6;
    letter-spacing: 0.02em;
    font-family: 'Georgia', serif;
    text-shadow: 
      0 2px 4px rgba(0, 0, 0, 0.8),
      0 4px 8px rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(10px);
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.1) 0%, 
      rgba(255, 255, 255, 0.05) 100%);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
`;

const SliderButtons = styled.div`
  position: absolute;
  bottom: 50px;
  right: 50px;
  display: flex;
  gap: 15px;
  z-index: 10;

  @media (max-width: 768px) {
    bottom: 30px;
    right: 30px;
    gap: 10px;
  }
`;

const arrowButtons = css`
  width: 60px;
  height: 60px;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05));
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  padding: 0;
  margin: 0;
  user-select: none;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);

  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.1));
    transform: scale(1.1) translateY(-2px);
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const PrevArrow = styled(IoArrowBack)`
  ${arrowButtons}
  font-size: 22px;
`;

const NextArrow = styled(IoArrowForward)`
  ${arrowButtons}
  font-size: 22px;
`;

const SliderDots = styled.div`
  position: absolute;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 15px;
  z-index: 10;

  @media (max-width: 768px) {
    bottom: 30px;
    gap: 10px;
  }
`;

const Dot = styled.button`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.6);
  background: ${props => props.active ? 
    'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))' : 
    'transparent'};
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.active ? 
    '0 4px 12px rgba(255, 255, 255, 0.3)' : 
    '0 2px 6px rgba(0, 0, 0, 0.2)'};
  
  &:hover {
    background: rgba(255, 255, 255, 0.8);
    transform: scale(1.3);
    border-color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 6px 20px rgba(255, 255, 255, 0.4);
  }

  @media (max-width: 768px) {
    width: 12px;
    height: 12px;
  }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0.8), 
    rgba(255, 255, 255, 0.4)
  );
  z-index: 10;
  width: ${props => props.progress}%;
  transition: width 0.1s linear;
  box-shadow: 0 -2px 10px rgba(255, 255, 255, 0.3);
`;

const Hero = ({ slides }) => {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState({});
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const length = slides.length;
  const timeout = useRef(null);
  const progressInterval = useRef(null);
  const slideInterval = 7000; // 7 seconds

  // Preload all images
  useEffect(() => {
    const preloadImages = async () => {
      const loadPromises = slides.map((slide, index) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            setPreloadedImages(prev => new Set([...prev, index]));
            resolve();
          };
          img.onerror = resolve; // Continue even if image fails
          img.src = slide.image;
        });
      });
      
      await Promise.all(loadPromises);
    };

    preloadImages();
  }, [slides]);

  // Progress bar and auto-advance
  useEffect(() => {
    setProgress(0);
    
    const startProgress = () => {
      progressInterval.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + (100 / (slideInterval / 100));
          if (newProgress >= 100) {
            setCurrent(current => (current === length - 1 ? 0 : current + 1));
            return 0;
          }
          return newProgress;
        });
      }, 100);
    };

    timeout.current = setTimeout(startProgress, 500);

    return () => {
      if (timeout.current) clearTimeout(timeout.current);
      if (progressInterval.current) clearInterval(progressInterval.current);
    };
  }, [current, length]);

  const nextSlide = () => {
    setCurrent(current === length - 1 ? 0 : current + 1);
    setProgress(0);
  };

  const prevSlide = () => {
    setCurrent(current === 0 ? length - 1 : current - 1);
    setProgress(0);
  };

  const goToSlide = (index) => {
    setCurrent(index);
    setProgress(0);
  };

  if (!Array.isArray(slides) || slides.length <= 0) {
    return null;
  }

  const fadeAnimation = {
    hidden: { opacity: 0, scale: 1.1 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        duration: 2,
        ease: [0.25, 0.1, 0.25, 1]
      } 
    },
    exit: { 
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 1
      }
    },
  };

  const textAnimation = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { 
        duration: 1.5,
        delay: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      } 
    },
    exit: { 
      opacity: 0,
      y: -30,
      scale: 1.1,
      transition: {
        duration: 0.8
      }
    },
  };

  return (
    <HeroSection>
      <HeroWrapper>
        <AnimatePresence mode="wait">
          {slides.map((slide, index) => {
            return (
              <HeroSlide key={index}>
                {index === current && (
                  <HeroSlider>
                    <ImageContainer>
                      {!preloadedImages.has(index) && <LoadingPlaceholder />}
                      <HeroImage
                        src={slide.image}
                        alt={slide.alt}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={fadeAnimation}
                        style={{ 
                          opacity: preloadedImages.has(index) ? 1 : 0,
                          transition: 'opacity 0.5s ease-in-out'
                        }}
                      />
                    </ImageContainer>
                    <HeroContent>
                      <motion.h3
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={textAnimation}
                        data-aos="fade-up"
                        data-aos-duration="1000"
                      >
                        {slide.title}
                      </motion.h3>
                      <motion.h2
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={{
                          ...textAnimation,
                          visible: {
                            ...textAnimation.visible,
                            transition: {
                              ...textAnimation.visible.transition,
                              delay: 0.8
                            }
                          }
                        }}
                        data-aos="fade-up"
                        data-aos-duration="1000"
                        data-aos-delay="300"
                      >
                        {slide.titletwo}
                      </motion.h2>
                    </HeroContent>
                  </HeroSlider>
                )}
              </HeroSlide>
            );
          })}
        </AnimatePresence>
        
        <SliderButtons>
          <PrevArrow onClick={prevSlide} />
          <NextArrow onClick={nextSlide} />
        </SliderButtons>

        <SliderDots>
          {slides.map((_, index) => (
            <Dot
              key={index}
              active={index === current}
              onClick={() => goToSlide(index)}
            />
          ))}
        </SliderDots>

        <ProgressBar progress={progress} />
      </HeroWrapper>
    </HeroSection>
  );
};

export default Hero;